import { Settings } from '@mui/icons-material';
import { Box, Button, IconButton, Slider, Switch } from '@mui/material';
import React, { useState } from 'react';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import { APICONSTANTS } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import axios from 'src/axios';
import { DEFAULT_CANVAS_PREFERENCES } from '../../pedigree-utils';
import { computeAutoSpacing } from '../pedigree/autoSpacing';
import { config } from '../pedigree/config';
import { getProbandState } from '../pedigree/probandState';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const CARD_SX = {
  bgcolor: '#fff',
  border: '0.5px solid',
  borderColor: 'divider',
  borderRadius: '12px',
  overflow: 'hidden'
};

const SECTION_LABEL_SX = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'text.disabled',
  mb: 1
};

const ROW_LABEL_W = 160;

const ConfigRow = ({ label, isLast, children }) => (
  <Box
    sx={{
      px: 2.5,
      py: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      borderBottom: isLast ? 'none' : '0.5px solid',
      borderColor: 'divider',
      '&:hover': { bgcolor: 'rgba(0,0,0,0.015)' }
    }}
  >
    <Box
      sx={{
        width: ROW_LABEL_W,
        flexShrink: 0,
        fontSize: 14,
        fontWeight: 500,
        color: 'text.primary'
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap'
      }}
    >
      {children}
    </Box>
  </Box>
);

function CanvasPreferences({
  options,
  setOptions,
  members = [],
  buttonRight,
  buttonLeft,
  inline = false
}) {
  const t = useAutoTranslation();

  const [open, setOpen] = useState(false);
  const [preferences, setPreferences] = useState({
    DEFAULT_CANVAS_PREFERENCES
  });

  React.useMemo(() => {
    setPreferences(options);
  }, [options]);

  const { setSnack } = React.useContext(SnackContext);

  const onSave = async () => {
    // `options` carries the live labelsSet, so this dialog was persisting a
    // whole snapshot of the label configuration into the user's canvas
    // preferences as a side effect of saving a font size. It only ever worked
    // because PedigreeBox re-applies the live labelsSet AFTER spreading these
    // preferences back in — a spread-order dependency nothing declared.
    // Label configuration belongs to presets (TGCLOUD-1663), so keep it out of
    // this blob entirely. Old stored blobs stay harmless for the same reason
    // they were harmless before: the live set still wins.
    // eslint-disable-next-line no-unused-vars
    const { labelsSet, ...canvasOnly } = preferences || {};
    return axios
      .put(APICONSTANTS.updatePreferences, {
        pedigreeSettings: canvasOnly
      })
      .then((res) => {
        if (res.status === 200) {
          setOptions((prev) => ({ ...prev, ...canvasOnly }));
          setSnack({
            status: true,
            msg: t('Pedigree preferences saved!'),
            severity: 'success'
          });
        }
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: t('Failed to save pedigree preferences.'),
          severity: 'error'
        });
      })
      .finally(() => setOpen(false));
  };

  return (
    <>
      {inline ? (
        <IconButton size="small" onClick={() => setOpen((prev) => !prev)}>
          <Settings sx={{ fontSize: 22, color: '#3d4551' }} />
        </IconButton>
      ) : (
        <Box
          {...(buttonLeft !== undefined
            ? { left: buttonLeft }
            : { right: buttonRight })}
          sx={{
            position: 'absolute',
            top: 12,
            pointerEvents: 'auto',
            zIndex: 1000
          }}
        >
          <IconButton onClick={() => setOpen((prev) => !prev)}>
            <Settings color="secondary" />
          </IconButton>
        </Box>
      )}
      <CustomDialog
        open={open}
        title="Pedigree Settings"
        handleClose={() => setOpen(false)}
        showActions={true}
        onSave={() => {
          return onSave();
        }}
        minHeight={400}
        distanceTop={150}
        allowBackgroundClose={true}
      >
        <PedigreeCanvasSettingsForm
          preferences={preferences}
          setPreferences={setPreferences}
          members={members}
        />
      </CustomDialog>
    </>
  );
}

const PedigreeCanvasSettingsForm = ({
  preferences,
  setPreferences,
  members
}) => {
  const t = useAutoTranslation();

  const autoOn = !!preferences.auto_spacing;

  // When Auto spacing is on, derive the H×V the chart will use from the current
  // labels (same pure function the renderer applies), to show read-only in the
  // disabled Spacing fields. Recomputes as font/symbol/labels change in-modal.
  const auto = React.useMemo(
    () =>
      autoOn && Array.isArray(members) && members.length
        ? computeAutoSpacing(members, {
            ...preferences,
            // Match the chart: proband-only mode suppresses labels on everyone
            // but the proband, and those labels must not drive the preview's
            // spacing when they do not drive the chart's (TGCLOUD-1610).
            probandPid: getProbandState(members).probandPid
          })
        : null,
    // Depend on the fields that affect the result, not the whole `preferences`
    // object (which changes on every keystroke in any field).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      autoOn,
      members,
      preferences.font_size,
      preferences.symbol_size,
      preferences.labelsSet
    ]
  );
  // Displayed slider values. With Auto spacing on these are the read-only
  // computed percentages; the vertical one is the TALLEST generation's, since
  // each generation now takes only the height its own labels need
  // (TGCLOUD-1610).
  const hValue = autoOn
    ? auto?.horizontalSpacingPct ?? preferences.horizontal_spacing
    : preferences.horizontal_spacing;
  const vValue = autoOn
    ? auto?.verticalSpacingPct ?? preferences.vertical_spacing
    : preferences.vertical_spacing;
  const perGenAuto = autoOn && config.ENABLE_PER_GENERATION_LEVEL_HEIGHT;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPreferences((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Sliders hide the raw px/% values (a clinician shouldn't have to reason about
  // pixels). The underlying preference keys + ranges are unchanged, so persistence
  // and applyCanvasPreferences are untouched.
  const setPref = (name, value) =>
    setPreferences((prev) => ({ ...prev, [name]: value }));

  const SLIDER_CAP_SX = {
    fontSize: 12,
    color: 'text.secondary',
    width: 60,
    flexShrink: 0
  };

  const handleReset = () => {
    setPreferences(DEFAULT_CANVAS_PREFERENCES);
  };

  return (
    <Box sx={{ pt: 1 }}>
      <Box sx={SECTION_LABEL_SX}>{t('Canvas')}</Box>
      <Box sx={CARD_SX}>
        <ConfigRow label={t('Symbol Size')}>
          <Box sx={SLIDER_CAP_SX}>{t('Smaller')}</Box>
          <Slider
            size="small"
            value={Number(preferences.symbol_size) || 155}
            onChange={(_e, v) => setPref('symbol_size', v)}
            min={100}
            max={200}
            step={5}
            sx={{ flex: 1, mx: 1 }}
          />
          <Box sx={{ ...SLIDER_CAP_SX, textAlign: 'right' }}>{t('Larger')}</Box>
        </ConfigRow>

        <ConfigRow label={t('Font Size')}>
          <Box sx={SLIDER_CAP_SX}>{t('Smaller')}</Box>
          <Slider
            size="small"
            value={Number(preferences.font_size) || 70}
            onChange={(_e, v) => setPref('font_size', v)}
            min={30}
            max={130}
            step={5}
            sx={{ flex: 1, mx: 1 }}
          />
          <Box sx={{ ...SLIDER_CAP_SX, textAlign: 'right' }}>{t('Larger')}</Box>
        </ConfigRow>

        {/* Separate axes (TGCLOUD-1610, Kathryn): spreading a chart wide enough
            to read full labels used to drag the vertical spacing out with it,
            because one slider wrote both. */}
        <ConfigRow label={t('Horizontal spacing')}>
          <Box sx={SLIDER_CAP_SX}>{t('Tighter')}</Box>
          <Slider
            size="small"
            value={Number(hValue) || 100}
            onChange={(_e, v) => setPref('horizontal_spacing', v)}
            min={100}
            max={500}
            step={50}
            disabled={autoOn}
            sx={{ flex: 1, mx: 1 }}
          />
          <Box sx={{ ...SLIDER_CAP_SX, textAlign: 'right' }}>
            {t('Spacious')}
          </Box>
          {autoOn && (
            <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
              {t('auto')}
            </Box>
          )}
        </ConfigRow>

        <ConfigRow label={t('Vertical spacing')}>
          <Box sx={SLIDER_CAP_SX}>{t('Tighter')}</Box>
          <Slider
            size="small"
            value={Number(vValue) || 100}
            onChange={(_e, v) => setPref('vertical_spacing', v)}
            min={100}
            max={500}
            step={50}
            disabled={autoOn}
            sx={{ flex: 1, mx: 1 }}
          />
          <Box sx={{ ...SLIDER_CAP_SX, textAlign: 'right' }}>
            {t('Spacious')}
          </Box>
          {autoOn && (
            <Box
              sx={{
                fontSize: 12,
                color: 'text.secondary',
                whiteSpace: 'nowrap'
              }}
            >
              {perGenAuto ? t('auto · per generation') : t('auto')}
            </Box>
          )}
        </ConfigRow>

        <ConfigRow label={t('Auto spacing')}>
          <Switch
            name="auto_spacing"
            checked={autoOn}
            onChange={handleChange}
            size="small"
          />
          <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
            {autoOn
              ? t('Spacing set automatically from labels')
              : t('Derive spacing from label density & length')}
          </Box>
        </ConfigRow>

        <ConfigRow label={t('Enable Grid')} isLast>
          <Switch
            name="enable_grid"
            checked={!!preferences.enable_grid}
            onChange={handleChange}
            size="small"
          />
        </ConfigRow>
      </Box>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="outlined" size="small" onClick={handleReset}>
          {t('Reset to defaults')}
        </Button>
      </Box>
    </Box>
  );
};

export default CanvasPreferences;
